import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import { registerUser } from '../../services/authService';
import { toast } from 'react-toastify';
import logo from '../../assets/logo.png';
import logger from '../../utils/logger';

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm();

  const watchPassword = watch('password');

  const onLoginSubmit = async (data) => {
    try {
      logger.logAuth('LOGIN_ATTEMPT', { username: data.username });
      setLoading(true);
      await login(data);
      logger.logAuth('LOGIN_SUCCESS', { username: data.username });
      navigate('/dashboard');
    } catch (error) {
      logger.logError(error, { action: 'login', username: data.username });
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  const onRegisterSubmit = async (data) => {
    try {
      logger.logAuth('REGISTER_ATTEMPT', { 
        username: data.username, 
        email: data.email,
        role: data.role 
      });
      setLoading(true);
      await registerUser(data);
      logger.logAuth('REGISTER_SUCCESS', { 
        username: data.username, 
        email: data.email,
        role: data.role 
      });
      toast.success('Registrasi berhasil! Silakan login.');
      setIsRegistering(false);
      reset();
    } catch (error) {
      logger.logError(error, { 
        action: 'register', 
        username: data.username,
        email: data.email 
      });
      console.error('Register error:', error);
      const message = error.response?.data?.message || 'Gagal melakukan registrasi';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-5">
            <div className="card shadow-lg border-0">
              <div className="card-body p-5">
                <div className="text-center mb-4">
                  <div className="logo-container justify-content-center mb-3">
                    <img src={logo} alt="SadarTalaud Logo" className="logo" />
                    <h4 className="mb-0">SadarTalaud</h4>
                  </div>
                  <h5 className="text-muted">Satu Data Rumah Talaud</h5>
                  <p className="text-muted small">Kabupaten Kepulauan Talaud</p>
                </div>

                <div className="text-center mb-4">
                  <div className="btn-group" role="group">
                    <button
                      type="button"
                      className={`btn ${!isRegistering ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setIsRegistering(false)}
                    >
                      Login
                    </button>
                    <button
                      type="button"
                      className={`btn ${isRegistering ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setIsRegistering(true)}
                    >
                      Daftar
                    </button>
                  </div>
                </div>

                {!isRegistering ? (
                  // Login Form
                  <form onSubmit={handleSubmit(onLoginSubmit)}>
                    <div className="mb-3">
                      <label htmlFor="username" className="form-label">
                        Username
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.username ? 'is-invalid' : ''}`}
                        id="username"
                        placeholder="Masukkan username"
                        {...register('username', { 
                          required: 'Username harus diisi' 
                        })}
                      />
                      {errors.username && (
                        <div className="invalid-feedback">
                          {errors.username.message}
                        </div>
                      )}
                    </div>

                    <div className="mb-4">
                      <label htmlFor="password" className="form-label">
                        Password
                      </label>
                      <input
                        type="password"
                        className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                        id="password"
                        placeholder="Masukkan password"
                        {...register('password', { 
                          required: 'Password harus diisi' 
                        })}
                      />
                      {errors.password && (
                        <div className="invalid-feedback">
                          {errors.password.message}
                        </div>
                      )}
                    </div>

                    <button
                      type="submit"
                      className="btn btn-primary w-100"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Loading...
                        </>
                      ) : (
                        'Login'
                      )}
                    </button>
                  </form>
                ) : (
                  // Register Form
                  <form onSubmit={handleSubmit(onRegisterSubmit)}>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label htmlFor="username" className="form-label">
                            Username *
                          </label>
                          <input
                            type="text"
                            className={`form-control ${errors.username ? 'is-invalid' : ''}`}
                            id="username"
                            placeholder="Masukkan username"
                            {...register('username', { 
                              required: 'Username harus diisi',
                              minLength: { value: 3, message: 'Username minimal 3 karakter' }
                            })}
                          />
                          {errors.username && (
                            <div className="invalid-feedback">
                              {errors.username.message}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label htmlFor="email" className="form-label">
                            Email *
                          </label>
                          <input
                            type="email"
                            className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                            id="email"
                            placeholder="Masukkan email"
                            {...register('email', { 
                              required: 'Email harus diisi',
                              pattern: {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: 'Email tidak valid'
                              }
                            })}
                          />
                          {errors.email && (
                            <div className="invalid-feedback">
                              {errors.email.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label htmlFor="full_name" className="form-label">
                        Nama Lengkap *
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.full_name ? 'is-invalid' : ''}`}
                        id="full_name"
                        placeholder="Masukkan nama lengkap"
                        {...register('full_name', { 
                          required: 'Nama lengkap harus diisi' 
                        })}
                      />
                      {errors.full_name && (
                        <div className="invalid-feedback">
                          {errors.full_name.message}
                        </div>
                      )}
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label htmlFor="password" className="form-label">
                            Password *
                          </label>
                          <input
                            type="password"
                            className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                            id="password"
                            placeholder="Masukkan password"
                            {...register('password', { 
                              required: 'Password harus diisi',
                              minLength: { value: 6, message: 'Password minimal 6 karakter' }
                            })}
                          />
                          {errors.password && (
                            <div className="invalid-feedback">
                              {errors.password.message}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label htmlFor="confirm_password" className="form-label">
                            Konfirmasi Password *
                          </label>
                          <input
                            type="password"
                            className={`form-control ${errors.confirm_password ? 'is-invalid' : ''}`}
                            id="confirm_password"
                            placeholder="Konfirmasi password"
                            {...register('confirm_password', { 
                              required: 'Konfirmasi password harus diisi',
                              validate: value => value === watchPassword || 'Password tidak cocok'
                            })}
                          />
                          {errors.confirm_password && (
                            <div className="invalid-feedback">
                              {errors.confirm_password.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="mb-4">
                      <label htmlFor="role" className="form-label">
                        Role *
                      </label>
                      <select
                        className={`form-select ${errors.role ? 'is-invalid' : ''}`}
                        id="role"
                        {...register('role', { 
                          required: 'Role harus dipilih' 
                        })}
                      >
                        <option value="">Pilih Role</option>
                        <option value="pengguna">Pengguna</option>
                        {/* <option value="peninjau">Peninjau</option>
                        <option value="admin">Admin</option> */}
                      </select>
                      {errors.role && (
                        <div className="invalid-feedback">
                          {errors.role.message}
                        </div>
                      )}
                    </div>

                    <button
                      type="submit"
                      className="btn btn-primary w-100"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Loading...
                        </>
                      ) : (
                        'Daftar'
                      )}
                    </button>
                  </form>
                )}

                <div className="text-center mt-4">
                  <small className="text-muted">
                    © 2025 SadarTalaud. All rights reserved.
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login; 